import { Extension } from '@tiptap/core';

const INDENT_STEP_IN = 0.25;
const INDENT_STEP_PX = 24;
const INDENT_STEP_PT = 18;
const MIN_INDENT = 0;
const MAX_INDENT = 12;
const TARGET_NODES = new Set(['paragraph', 'heading']);

const clampIndent = (value: number): number => Math.max(MIN_INDENT, Math.min(MAX_INDENT, value));

const parseIndentFromStyle = (style: string | null | undefined): number => {
  if (!style) return 0;
  const m = /margin-left\s*:\s*([\d.]+)\s*(in|px|pt|rem|em)\b/i.exec(style);
  if (!m) return 0;
  const value = Number(m[1] || 0);
  const unit = String(m[2] || '').toLowerCase();
  if (!Number.isFinite(value) || value <= 0) return 0;

  if (unit === 'in') return clampIndent(Math.round(value / INDENT_STEP_IN));
  if (unit === 'px') return clampIndent(Math.round(value / INDENT_STEP_PX));
  if (unit === 'pt') return clampIndent(Math.round(value / INDENT_STEP_PT));
  if (unit === 'rem' || unit === 'em') return clampIndent(Math.round((value * 16) / INDENT_STEP_PX));
  return 0;
};

const nextIndent = (current: unknown, delta: number): number => {
  const safeCurrent = Number(current) || 0;
  return clampIndent(safeCurrent + delta);
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    indent: {
      increaseIndent: () => ReturnType;
      decreaseIndent: () => ReturnType;
    };
  }
}

function updateCurrentBlockIndent(state: any, tr: any, delta: number): boolean {
  const { $from } = state.selection;
  for (let depth = $from.depth; depth > 0; depth--) {
    const node = $from.node(depth);
    if (!TARGET_NODES.has(node.type.name)) continue;
    const pos = $from.before(depth);
    const newIndent = nextIndent(node.attrs.indent, delta);
    const currentIndent = Number(node.attrs.indent) || 0;
    if (newIndent === currentIndent) return false;
    tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: newIndent });
    return true;
  }
  return false;
}

function updateSelectionIndent(state: any, tr: any, delta: number): boolean {
  const { from, to, empty } = state.selection;
  if (empty) return updateCurrentBlockIndent(state, tr, delta);

  let changed = false;
  state.doc.nodesBetween(from, to, (node: any, pos: number) => {
    if (!TARGET_NODES.has(node.type.name)) return;
    const newIndent = nextIndent(node.attrs.indent, delta);
    const currentIndent = Number(node.attrs.indent) || 0;
    if (newIndent === currentIndent) return;
    tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: newIndent });
    changed = true;
    return false;
  });
  return changed;
}

export const Indent = Extension.create({
  name: 'indent',

  addGlobalAttributes() {
    return [
      {
        types: ['paragraph', 'heading'],
        attributes: {
          indent: {
            default: 0,
            parseHTML: (element: HTMLElement) => {
              const dataIndent = Number(element.getAttribute('data-indent') || 0);
              if (Number.isFinite(dataIndent) && dataIndent > 0) return clampIndent(Math.round(dataIndent));
              return parseIndentFromStyle(element.getAttribute('style'));
            },
            renderHTML: (attributes: Record<string, unknown>) => {
              const indent = clampIndent(Number(attributes.indent) || 0);
              if (!indent) return {};
              return {
                'data-indent': String(indent),
                style: `margin-left: ${indent * INDENT_STEP_IN}in;`
              };
            }
          }
        }
      }
    ];
  },

  addCommands() {
    return {
      increaseIndent:
        () =>
        ({ state, dispatch }: any) => {
          const tr = state.tr;
          const changed = updateSelectionIndent(state, tr, 1);
          if (!changed) return false;
          if (dispatch) dispatch(tr.scrollIntoView());
          return true;
        },
      decreaseIndent:
        () =>
        ({ state, dispatch }: any) => {
          const tr = state.tr;
          const changed = updateSelectionIndent(state, tr, -1);
          if (!changed) return false;
          if (dispatch) dispatch(tr.scrollIntoView());
          return true;
        }
    };
  },

  addKeyboardShortcuts() {
    return {
      // Backspace at the VERY START of an indented paragraph/heading first
      // OUTDENTS one level (stepping the cursor back toward the line start)
      // instead of merging into the previous line. Once the indent reaches 0
      // we return false so the editor's default Backspace (join with the
      // previous block) runs as normal. This lets the user reach the left
      // margin and type from the line start.
      Backspace: ({ editor }: any) => {
        const { selection } = editor.state;
        const { $from, empty } = selection;
        if (!empty || $from.parentOffset !== 0) return false;
        const node = $from.parent;
        if (!TARGET_NODES.has(node.type.name)) return false;
        if ((Number(node.attrs.indent) || 0) <= 0) return false;
        return editor.commands.decreaseIndent();
      }
    };
  }
});
